/**
 * Nudge sounds. Built-in presets are synthesised here (no audio files, no third-party recordings).
 * A user may instead pick their OWN sound file from their device: it is kept only in this browser
 * (localStorage, ≤ 1 MB) and played only for them — it is never uploaded or shared.
 */

export type NudgePreset = 'buzz' | 'rattle' | 'knock' | 'custom';

export const NUDGE_PRESETS: { id: Exclude<NudgePreset, 'custom'>; label: string }[] = [
  { id: 'knock', label: 'Kapı tıklatma' },
  { id: 'buzz', label: 'Kalın zırıltı' },
  { id: 'rattle', label: 'Masada titreyen telefon' },
];

/** Default chosen by the product owner (2026-10-13). */
export const DEFAULT_NUDGE: Exclude<NudgePreset, 'custom'> = 'knock';

/** Deterministic PRNG so a preset sounds identical every time (and is testable). */
function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

/** One-pole low-pass; applied twice gives a soft 12 dB/oct roll-off (no tinny highs). */
function lowpass(cut: number, sr: number) {
  const a = Math.exp((-2 * Math.PI * cut) / sr);
  let y1 = 0,
    y2 = 0;
  return (x: number) => {
    y1 = (1 - a) * x + a * y1;
    y2 = (1 - a) * y1 + a * y2;
    return y2;
  };
}

/** Removes DC and sub-audio rumble so small speakers do not distort. */
function dcBlock() {
  let x1 = 0,
    y1 = 0;
  return (x: number) => {
    const y = x - x1 + 0.995 * y1;
    x1 = x;
    y1 = y;
    return y;
  };
}

function normalise(out: Float32Array, peakTarget = 0.7) {
  let peak = 0;
  for (const v of out) peak = Math.max(peak, Math.abs(v));
  if (peak > 0) for (let i = 0; i < out.length; i++) out[i] = (out[i]! / peak) * peakTarget;
  return out;
}

/** Soft-edged window: 1 inside [a,b], ramps of `atk` / `rel` seconds. */
const gate = (t: number, a: number, b: number, atk = 0.012, rel = 0.04) =>
  Math.min(1, Math.max(0, (t - a) / atk)) * Math.min(1, Math.max(0, (b - t) / rel));

/** Deep "bzzzt — bzzt": a thick, low buzzer (≈ 70 Hz) shaken at ~18 Hz. Nothing above ~900 Hz. */
function buzz(sr: number): Float32Array {
  const dur = 0.78;
  const out = new Float32Array(Math.floor(sr * dur));
  const lp = lowpass(900, sr);
  const dc = dcBlock();
  let ph = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    const f = 68 + 6 * Math.sin(2 * Math.PI * 5 * t); // slight wobble like a motor under load
    ph += f / sr;
    const p = ph - Math.floor(ph);
    const saw = 2 * p - 1;
    const sq = p < 0.5 ? 1 : -1;
    const shake = 0.55 + 0.45 * Math.sin(2 * Math.PI * 18 * t); // the "shaking" pulse
    const env = Math.max(gate(t, 0, 0.42), 0.9 * gate(t, 0.5, 0.78));
    out[i] = dc(lp(env * shake * (0.65 * saw + 0.35 * sq)));
  }
  return normalise(out);
}

/** A phone vibrating on a desk: low hum plus soft, muffled knocks of the casing. */
function rattle(sr: number): Float32Array {
  const dur = 0.82;
  const out = new Float32Array(Math.floor(sr * dur));
  const rand = rng(0x5eed);
  const lpN = lowpass(850, sr);
  const lpOut = lowpass(1100, sr);
  const dc = dcBlock();
  let ph = 0;
  let prev = 0;
  let hit = 0;
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    ph += (30 + 2 * Math.sin(2 * Math.PI * 3 * t)) / sr;
    const cyc = Math.floor(ph);
    if (cyc !== prev) {
      hit = 0.8 + 0.2 * rand();
      prev = cyc;
    }
    hit *= Math.exp(-1 / (sr * 0.012));
    const knock = hit * lpN(rand() * 2 - 1) * 3;
    const hum = 0.35 * Math.sin(2 * Math.PI * 4 * ph) + 0.25 * Math.sin(2 * Math.PI * 8 * ph);
    const env = Math.max(gate(t, 0, 0.46), 0.85 * gate(t, 0.54, 0.82));
    out[i] = dc(lpOut(env * (0.6 * knock + 0.4 * hum)));
  }
  return normalise(out);
}

/** Three quick knocks "tak-tak-tak" (wooden, low). */
function knock(sr: number): Float32Array {
  const dur = 0.62;
  const out = new Float32Array(Math.floor(sr * dur));
  const rand = rng(0xbeef);
  const lp = lowpass(1400, sr);
  const dc = dcBlock();
  const hits = [0, 0.17, 0.34];
  for (let i = 0; i < out.length; i++) {
    const t = i / sr;
    let v = 0;
    for (const h of hits) {
      const d = t - h;
      if (d < 0) continue;
      const e = Math.exp(-d / 0.045);
      v +=
        e * (Math.sin(2 * Math.PI * 140 * d) + 0.5 * Math.sin(2 * Math.PI * 260 * d)) +
        0.6 * Math.exp(-d / 0.004) * (rand() * 2 - 1);
    }
    out[i] = dc(lp(v));
  }
  return normalise(out);
}

export function presetSamples(id: Exclude<NudgePreset, 'custom'>, sr: number): Float32Array {
  return id === 'rattle' ? rattle(sr) : id === 'knock' ? knock(sr) : buzz(sr);
}

/** Kept for tests/back-compat: the default nudge sound. */
export const nudgeSamples = (sr: number) => presetSamples(DEFAULT_NUDGE, sr);

/* ---------- preference (this device only) ---------- */

const PREF_KEY = 'cr.nudge.sound';
const CUSTOM_KEY = 'cr.nudge.custom';
export const CUSTOM_MAX_BYTES = 1024 * 1024;

export function getNudgePreset(): NudgePreset {
  try {
    const v = localStorage.getItem(PREF_KEY);
    if (v === 'custom' && localStorage.getItem(CUSTOM_KEY)) return 'custom';
    if (v === 'rattle' || v === 'knock' || v === 'buzz') return v;
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_NUDGE;
}

export function setNudgePreset(p: NudgePreset): void {
  try {
    localStorage.setItem(PREF_KEY, p);
  } catch {
    /* not remembered */
  }
}

export function customSoundName(): string | null {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    return raw ? (JSON.parse(raw) as { name: string }).name : null;
  } catch {
    return null;
  }
}

let customBuffer: AudioBuffer | null = null;

/** Stores the user's own sound file on this device after checking that the browser can decode it. */
export async function setCustomSound(file: File): Promise<'ok' | 'too_large' | 'not_audio' | 'storage'> {
  if (file.size > CUSTOM_MAX_BYTES) return 'too_large';
  const bytes = new Uint8Array(await file.arrayBuffer());
  const c = audio();
  try {
    if (!c) return 'not_audio';
    customBuffer = await c.decodeAudioData(bytes.slice().buffer);
  } catch {
    return 'not_audio';
  }
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  try {
    localStorage.setItem(CUSTOM_KEY, JSON.stringify({ name: file.name.slice(0, 80), data: btoa(bin) }));
    localStorage.setItem(PREF_KEY, 'custom');
  } catch {
    customBuffer = null;
    return 'storage';
  }
  return 'ok';
}

export function clearCustomSound(): void {
  customBuffer = null;
  try {
    localStorage.removeItem(CUSTOM_KEY);
    if (localStorage.getItem(PREF_KEY) === 'custom') localStorage.setItem(PREF_KEY, DEFAULT_NUDGE);
  } catch {
    /* ignore */
  }
}

async function loadCustom(c: AudioContext): Promise<AudioBuffer | null> {
  if (customBuffer) return customBuffer;
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    if (!raw) return null;
    const bin = atob((JSON.parse(raw) as { data: string }).data);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    customBuffer = await c.decodeAudioData(bytes.buffer);
    return customBuffer;
  } catch {
    return null;
  }
}

/* ---------- playback ---------- */

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const Ctx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;
  ctx = new Ctx();
  return ctx;
}

/**
 * Browsers only allow sound after the user has interacted with the page once. Unlock the shared audio
 * context on the first click/tap/key so a later nudge can be heard even if it arrives without interaction.
 */
export function unlockAudioOnFirstGesture(): void {
  const unlock = () => {
    const c = audio();
    void c?.resume();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}

export async function playNudge(preset: NudgePreset = getNudgePreset()): Promise<void> {
  try {
    const c = audio();
    if (!c) return;
    void c.resume();
    let buf: AudioBuffer | null = null;
    if (preset === 'custom') buf = await loadCustom(c);
    if (!buf) {
      const data = presetSamples(preset === 'custom' ? DEFAULT_NUDGE : preset, c.sampleRate);
      buf = c.createBuffer(1, data.length, c.sampleRate);
      buf.getChannelData(0).set(data);
    }
    const src = c.createBufferSource();
    src.buffer = buf;
    src.connect(c.destination);
    src.start();
    src.stop(c.currentTime + 4); // a long custom file never plays for more than 4 s
  } catch {
    /* audio unavailable: the visual alert still shows */
  }
}
